import * as Sentry from '@sentry/react';

const STORAGE_KEY = 'sentryConfig';

// Bump whenever DEFAULT_CONFIG changes in a way that must win over a user's
// stored config. Anything persisted under an older (or missing) version is
// discarded on load rather than merged, so stale settings can't quietly
// override new defaults. v2: console `log`/`info` dropped from logLevels,
// replay moved to errors-only.
const CONFIG_VERSION = 2;

interface StoredSentryConfig extends Partial<SentryConfigOptions> {
    version?: number;
}

export interface SentryConfigOptions {
    enabled: boolean;
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    logLevels: string[];
    tracingEnabled: boolean;
    replayEnabled: boolean;
}

const DEFAULT_CONFIG: SentryConfigOptions = {
    enabled: true,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Sentry log levels: trace | debug | info | warn | error | fatal.
    // `console.log` arrives as `info`, so omitting it drops the bulk of volume.
    logLevels: ['warn', 'error', 'fatal'],
    tracingEnabled: true,
    replayEnabled: true,
};

class SentryConfigService {
    private static instance: SentryConfigService;
    private config: SentryConfigOptions = { ...DEFAULT_CONFIG };

    static getInstance(): SentryConfigService {
        if (!SentryConfigService.instance) {
            SentryConfigService.instance = new SentryConfigService();
            SentryConfigService.instance.loadFromStorage();
        }
        return SentryConfigService.instance;
    }

    private loadFromStorage(): void {
        if (typeof window === 'undefined') return;
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (!stored) return;

            const parsed = JSON.parse(stored) as StoredSentryConfig;
            const { version, ...storedConfig } = parsed;

            if (version !== CONFIG_VERSION) {
                // Stale schema. Take the new defaults, but honour an explicit
                // opt-out — resurrecting Sentry for someone who turned it off
                // would raise volume, which is the opposite of the intent.
                this.config = { ...DEFAULT_CONFIG };
                if (storedConfig.enabled === false) this.config.enabled = false;
                this.saveToStorage();
                return;
            }

            this.config = { ...DEFAULT_CONFIG, ...storedConfig };
        } catch {
            this.config = { ...DEFAULT_CONFIG };
        }
    }

    private saveToStorage(): void {
        if (typeof window === 'undefined') return;
        try {
            const payload: StoredSentryConfig = { ...this.config, version: CONFIG_VERSION };
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch {}
    }

    getConfig(): SentryConfigOptions {
        return { ...this.config };
    }

    updateConfig(updates: Partial<SentryConfigOptions>): void {
        this.config = { ...this.config, ...updates };
        this.saveToStorage();
    }

    resetConfig(): void {
        this.config = { ...DEFAULT_CONFIG };
        this.saveToStorage();
    }

    isEnabled(): boolean {
        return this.config.enabled;
    }

    isTracingEnabled(): boolean {
        return this.config.enabled && this.config.tracingEnabled;
    }

    getTracesSampleRate(): number {
        if (!this.config.enabled || !this.config.tracingEnabled) return 0;
        return this.config.tracesSampleRate;
    }

    isReplayEnabled(): boolean {
        return this.config.enabled && this.config.replayEnabled;
    }

    shouldCaptureLogLevel(level: string): boolean {
        return this.config.enabled && this.config.logLevels.includes(level);
    }

    setUserContext(context: {
        userId?: string;
        username?: string;
        email?: string;
        tenantId?: string;
        employeeId?: string;
        companyName?: string;
    }): void {
        Sentry.setUser({
            id: context.userId,
            username: context.username,
            email: context.email,
            tenantId: context.tenantId,
            employeeId: context.employeeId,
            companyName: context.companyName,
        } as any);
    }

    clearUserContext(): void {
        Sentry.setUser(null);
    }

    }

const sentryConfigService = SentryConfigService.getInstance();


export const SentryDynamicConfig = {
    tracesSampler: (_samplingContext: any): number => {
        return sentryConfigService.getTracesSampleRate();
    },
    beforeSend: (event: any, _hint: any): any | null => {
        if (!sentryConfigService.isEnabled()) return null;
        return event;
    },
    beforeSendTransaction: (event: any): any | null => {
        if (!sentryConfigService.isTracingEnabled()) return null;
        return event;
    },
};

export default sentryConfigService;
